import { useMutation, useQuery, useQueryClient } from 'react-query';
import { chamarOktonBank, enviarOktonBank } from '../oktonbank';

// Os nomes acompanham as colunas do IFLEX, então a origem de cada campo fica
// explícita e o mesmo vocabulário vale do banco até a tela.
// Tudo chega como string: o MySQL devolve número assim, e a conversão fica na
// tela, onde o valor é formatado ou somado.
export interface ContaBancaria {
  id_cts: string;
  filial_cts: string;
  descricao_cts: string;
  numero_cts: string;
  digito_cts: string;
  tipo_cts: string;
  agencia_cts: string | null;
  // A Moeda do próprio banco da Conta, a principal entre as que caem nela.
  // Mora em contasmoeda, daí o sufixo _ctm: contas não tem mais coluna de
  // Moeda.
  moeda_ctm: string | null;
  saldoinicial_cts: string;
  id_ban: string | null;
  nome_ban: string | null;
  confcomp_ban: string | null;
  agencia_agc: string | null;
  digito_agc: string | null;
  posto_agc: string | null;
  nome_moe: string | null;
}

interface IResponseData {
  rows: ContaBancaria[];
}

async function getContasBancarias(): Promise<ContaBancaria[]> {
  const dados = await chamarOktonBank<IResponseData>('OBContas', 'listar');
  return Array.isArray(dados?.rows) ? dados.rows : [];
}

export function useContasBancarias() {
  // staleTime curto de propósito: a conta é cadastrada no IFLEX, fora desta
  // tela. Com o refetchOnWindowFocus padrão do react-query, voltar da aba do
  // IFLEX revalida a lista — com 5 minutos, o usuário via dado velho e podia
  // agir sobre conta que já não existe mais.
  return useQuery(['contas-bancarias'], () => getContasBancarias(), {
    staleTime: 1000 * 5,
  });
}

// Opções dos seletores do formulário. Vêm do IFLEX pelo próprio módulo do
// OktonBank para não depender da permissão dos módulos Banco e Agência.
// confcomp_ban é o código de compensação: o IFLEX usa o 748 (Sicredi) para
// decidir se a Agência tem Dígito ou Posto.
export const BANCO_COM_POSTO = '748';

// Idem, para a Consulta de Extrato via API: bancos com essa integração, pelo
// código de compensação, e o nome que aparece na tela.
export const BANCO_SICOOB = '756';
export const BANCO_BRADESCO = '237';
export const BANCO_SANTANDER = '033';
export type BancoExtratoApi =
  | typeof BANCO_SICOOB
  | typeof BANCO_BRADESCO
  | typeof BANCO_SANTANDER;
export const BANCOS_EXTRATO_API: Record<BancoExtratoApi, string> = {
  [BANCO_SICOOB]: 'Sicoob',
  [BANCO_BRADESCO]: 'Bradesco',
  [BANCO_SANTANDER]: 'Santander',
};

// O Santander aparece como '33' ou '033' conforme o cadastro do Banco.
export function bancoComExtratoApi(
  confcompBan: string | null | undefined,
): BancoExtratoApi | undefined {
  const codigo = (confcompBan ?? '').trim().padStart(3, '0');
  return confcompBan && codigo in BANCOS_EXTRATO_API
    ? (codigo as BancoExtratoApi)
    : undefined;
}

export interface OpcaoBanco {
  id_ban: string;
  nome_ban: string;
  confcomp_ban: string | null;
}

// Valor sentinela do seletor: pede ao back que cadastre a Moeda junto com a
// Conta, usando o nome do Banco selecionado.
export const MOEDA_NOVA = 'nova';

// Valor sentinela do seletor: pede ao back que cadastre a Agência junto com a
// Conta, usando o número informado e o Banco selecionado.
export const AGENCIA_NOVA = 'nova';

export interface OpcaoAgencia {
  id_agc: string;
  agencia_agc: string;
  digito_agc: string | null;
  posto_agc: string | null;
  descricao_agc: string | null;
}

// O IFLEX exige Dígito e Posto na Agência, então gravamos 0 no que o usuário
// não informa. Esse 0 é preenchimento, não faz parte da numeração: exibir
// "1234-0" só confundiria quem leu a agência no extrato.
export function identificacaoAgencia(
  agencia: string | null,
  digito: string | null,
  posto: string | null,
): string {
  const sufixo = [digito, posto].find(v => !!v && v !== '0');
  return sufixo ? `${agencia}-${sufixo}` : agencia ?? '';
}

export interface OpcaoMoeda {
  id_moe: string;
  nome_moe: string;
}

export interface OpcaoTipoConta {
  valor: number;
  descricao: string;
}

export function useBancos() {
  return useQuery(
    ['oktonbank-bancos'],
    async () => {
      const dados = await chamarOktonBank<{ rows: OpcaoBanco[] }>(
        'OBContas',
        'listarBancos',
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    { staleTime: 1000 * 30 },
  );
}

// A lista muda conforme a conta em edição: o back devolve as Moedas livres
// mais a da própria conta, então a chave da query inclui o id.
// Só Moeda do mesmo Banco da Conta: uma Moeda do Sicoob numa conta do Bradesco
// deixaria extrato e financeiro em bancos diferentes, sem nada acusando. No
// cadastro a Conta ainda não existe, então o Banco vem do formulário.
export function useMoedasDisponiveis(contaId?: string, banco?: string) {
  return useQuery(
    ['oktonbank-moedas', contaId ?? '', banco ?? ''],
    async () => {
      const dados = await chamarOktonBank<{ rows: OpcaoMoeda[] }>(
        'OBContas',
        'listarMoedas',
        { conta: contaId ?? '', banco: banco ?? '' },
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    { enabled: !!banco || !!contaId },
  );
}

export function useAgencias() {
  return useQuery(
    ['oktonbank-agencias'],
    async () => {
      const dados = await chamarOktonBank<{ rows: OpcaoAgencia[] }>(
        'OBContas',
        'listarAgencias',
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    { staleTime: 1000 * 30 },
  );
}
export function useTiposConta() {
  return useQuery(
    ['oktonbank-tipos-conta'],
    async () => {
      const dados = await chamarOktonBank<{ rows: OpcaoTipoConta[] }>(
        'OBContas',
        'listarTipos',
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    { staleTime: Infinity },
  );
}

// ─── Moedas da Conta ───
// Uma Conta liquida várias Moedas, todas do tipo Banco: a principal, que Saque,
// Depósito e Transferência usam, e as demais — "PIX SICOOB", "CRÉDITO SICOOB".
// É o nome delas que diz por onde o dinheiro entrou.
export interface MoedaVinculada {
  id_ctm: string;
  moeda_ctm: string;
  principal_ctm: string;
  nome_moe: string;
  nome_tpt: string | null;
  tipodocumento_tpt: string | null;
}

export interface MoedaVinculavel {
  id_moe: string;
  nome_moe: string;
  nome_tpt: string | null;
  tipodocumento_tpt: string | null;
}

export function useVinculosDaConta(idConta?: string) {
  return useQuery(
    ['oktonbank-vinculos', idConta ?? ''],
    async () => {
      const dados = await chamarOktonBank<{ rows: MoedaVinculada[] }>(
        'OBContas',
        'listarVinculos',
        { conta: idConta || '' },
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    { enabled: !!idConta },
  );
}

// As Moedas ainda livres e do mesmo Banco da Conta. A principal não aparece
// aqui: ela vem do cadastro da Conta, não desta escolha.
export function useMoedasVinculaveis(idConta?: string) {
  return useQuery(
    ['oktonbank-vinculaveis', idConta ?? ''],
    async () => {
      const dados = await chamarOktonBank<{ rows: MoedaVinculavel[] }>(
        'OBContas',
        'listarVinculaveis',
        { conta: idConta ?? '' },
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    { enabled: !!idConta },
  );
}

export function useSaveVinculos() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ conta, moedas }: { conta: string; moedas: string[] }) =>
      enviarOktonBank('OBContas', 'gravarVinculos', {
        conta,
        moedas: moedas.join(','),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('oktonbank-vinculos');
        queryClient.invalidateQueries('oktonbank-vinculaveis');
        queryClient.invalidateQueries('oktonbank-moedas');
        queryClient.invalidateQueries('contas-bancarias');
        queryClient.invalidateQueries('monitor-bancario-resumo');
      },
    },
  );
}
